/**
 * Inactive Page
 *
 * Shown to users whose subscription has expired or account is inactive
 */

import { useAuth } from '../../hooks/useAuth';

export function Inactive() {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  // Format subscription end date if available
  const subscriptionEndDate = user.subscription_end_date
    ? new Date(user.subscription_end_date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null;

  return (
    <div class="flex items-center justify-center py-12">
      <div class="max-w-2xl w-full">
          <div class="bg-white shadow-lg rounded-lg overflow-hidden">
            {/* Warning header */}
            <div class="bg-gray-50 px-6 py-12 text-center">
              <div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-4">
                <svg class="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 class="text-3xl font-bold text-gray-900 mb-2">
                Cuenta Inactiva
              </h2>
              <p class="text-lg text-gray-600">
                {subscriptionEndDate
                  ? `Tu suscripción expiró el ${subscriptionEndDate}`
                  : 'Tu cuenta está actualmente inactiva'}
              </p>
            </div>

            {/* Information section */}
            <div class="px-6 py-8">
              <div class="space-y-6">
                {/* Status explanation */}
                <div class="bg-yellow-50 rounded-lg p-4">
                  <h3 class="text-sm font-semibold text-yellow-800 mb-2">
                    ¿Por qué está inactiva mi cuenta?
                  </h3>
                  <p class="text-yellow-700 text-sm">
                    Las cuentas de instructor en FIT-Book requieren una suscripción anual activa.
                    Tu suscripción ha expirado o tu cuenta ha sido suspendida temporalmente.
                  </p>
                </div>

                {/* Renewal information */}
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 mb-3">
                    Renueva tu Membresía
                  </h3>
                  <p class="text-gray-600 mb-4">
                    Para reactivar tu cuenta y volver a aparecer en el directorio, necesitas renovar tu suscripción anual.
                  </p>

                  <div class="bg-blue-50 rounded-lg p-4">
                    <div class="flex items-start">
                      <div class="flex-shrink-0">
                        <svg class="h-6 w-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      </div>
                      <div class="ml-4 flex-1">
                        <h4 class="font-medium text-blue-900 mb-2">
                          Cuota de Renovación Anual
                        </h4>
                        <p class="text-3xl font-bold text-blue-900 mb-2">120€</p>
                        <p class="text-sm text-blue-700 mb-3">
                          Incluye acceso completo al directorio y todas las funcionalidades por un año.
                        </p>
                        <a
                          href="mailto:pagos@fitbook.es?subject=Renovación de Suscripción - FIT-${user.id}"
                          class="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-lg transition"
                        >
                          Contactar para Renovar
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* What you're missing */}
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 mb-3">
                    Mientras tu cuenta esté inactiva:
                  </h3>
                  <div class="space-y-2">
                    <div class="flex items-start">
                      <svg class="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <p class="text-gray-600 text-sm">
                        No aparecerás en el directorio de instructores
                      </p>
                    </div>
                    <div class="flex items-start">
                      <svg class="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <p class="text-gray-600 text-sm">
                        Otros instructores no podrán ver tu perfil
                      </p>
                    </div>
                    <div class="flex items-start">
                      <svg class="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <p class="text-gray-600 text-sm">
                        No tendrás acceso al catálogo de recursos y cursos
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contact */}
                <div class="bg-blue-50 border-l-4 border-blue-400 p-4">
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="ml-3">
                      <h4 class="text-sm font-semibold text-blue-800 mb-1">
                        ¿Necesitas ayuda?
                      </h4>
                      <p class="text-sm text-blue-700">
                        Si tienes preguntas sobre tu cuenta o el proceso de renovación, contáctanos en{' '}
                        <a href="mailto:soporte@fitbook.es" class="font-medium underline">
                          soporte@fitbook.es
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* User status */}
          <div class="mt-6 bg-white shadow rounded-lg px-6 py-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-500">Usuario</p>
                <p class="text-lg font-medium text-gray-900">{user.email}</p>
                {subscriptionEndDate && (
                  <p class="text-xs text-gray-500 mt-1">
                    Expiró: {subscriptionEndDate}
                  </p>
                )}
              </div>
              <div class="text-right">
                <p class="text-sm text-gray-500">Estado actual</p>
                <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-800">
                  Cuenta Inactiva
                </span>
              </div>
            </div>
          </div>
      </div>
    </div>
  );
}
